import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createCRMTag,
  getCRMTags,
  manageCRMContactTags,
  manageEmployeeTags,
} from '../api';
import { syncPipelineBoardContact } from '../utils/pipelineSync';
import { patchEmployeeInCache } from '../../utils/employeeCache';
import toast from 'react-hot-toast';
import { parseApiError } from '@/common/utils';
import { useMobileScrollLock } from '@/common/hooks';

function getTagAssignErrorMessage(error, notFoundLabel) {
  const status = error?.response?.status;
  if (status === 404) return `${notFoundLabel} not found.`;
  const { message } = parseApiError(error);
  if (status === 400) return message || 'Invalid tag selection.';
  return message || 'Failed to update tags.';
}

function flattenPageResults(data) {
  return (
    data?.pages?.flatMap((page) =>
      Array.isArray(page) ? page : page?.results || [],
    ) || []
  );
}

function resolveContactTagIds(contactTags) {
  if (!Array.isArray(contactTags)) return [];
  return contactTags
    .map((tag) => {
      if (typeof tag === 'object' && tag?.id != null) return Number(tag.id);
      return null;
    })
    .filter((id) => id != null && !Number.isNaN(id));
}

function resolveContactTagItems(contactTags) {
  if (!Array.isArray(contactTags)) return [];
  return contactTags
    .map((tag) => {
      if (typeof tag === 'object' && tag?.name) {
        return {
          id: tag.id != null ? Number(tag.id) : null,
          name: String(tag.name),
          color: tag.color,
        };
      }
      if (typeof tag === 'string' && tag.trim()) {
        return { id: null, name: tag.trim(), color: null };
      }
      return null;
    })
    .filter(Boolean);
}

const TAG_COLORS = [
  '#3b82f6',
  '#f97316',
  '#22c55e',
  '#a855f7',
  '#ef4444',
  '#eab308',
  '#ec4899',
  '#6b7280',
];

function randomTagColor() {
  return TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];
}

function ChevronIcon({ open }) {
  return (
    <svg
      className={`w-4 h-4 flex-shrink-0 transition-transform text-[#50AB3D] ${open ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

export function InlineTagSelector({
  companyId,
  contactId,
  employeeId,
  contactTags = [],
  tags,
  disabled = false,
  localOnly = false,
  instanceId,
  emptyLabel = 'No tags assigned',
  placeholder = 'Search and select tags...',
  onTagsChange,
  onAssigned,
  appearance = 'field',
}) {
  const queryClient = useQueryClient();
  const generatedId = useId();
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const addTagsRef = useRef(null);
  const entityId = employeeId ?? contactId;
  const entityTags = tags ?? contactTags;
  const searchInputId = `inline-tag-search-${instanceId || entityId || generatedId}`;
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [draftTagIds, setDraftTagIds] = useState(() =>
    resolveContactTagIds(entityTags),
  );
  const [localCreatedTags, setLocalCreatedTags] = useState([]);
  const hasUserEditedRef = useRef(false);
  const [dropdownStyle, setDropdownStyle] = useState({
    position: 'fixed',
    top: -9999,
    left: -9999,
  });

  const savedTagIds = useMemo(
    () => resolveContactTagIds(entityTags),
    [entityTags],
  );
  const savedTagItems = useMemo(
    () => resolveContactTagItems(entityTags),
    [entityTags],
  );

  useEffect(() => {
    setDraftTagIds(savedTagIds);
  }, [savedTagIds]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useMobileScrollLock(isOpen);

  const {
    data: tagsData,
    isLoading: isTagsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['crm-tags', companyId, debouncedSearch],
    queryFn: async ({ pageParam = 1 }) => {
      const params = debouncedSearch
        ? { search: debouncedSearch, page: pageParam }
        : { page: pageParam };
      return getCRMTags(companyId, params);
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.next) return undefined;
      try {
        const url = new URL(lastPage.next);
        return url.searchParams.get('page') || undefined;
      } catch {
        const match = String(lastPage.next).match(/[?&]page=(\d+)/);
        return match ? match[1] : undefined;
      }
    },
    enabled: !!companyId && isOpen,
  });

  const availableTags = useMemo(() => {
    const byId = new Map();
    flattenPageResults(tagsData).forEach((tag) => {
      byId.set(String(tag.id), tag);
    });
    savedTagItems.forEach((tag) => {
      if (tag.id != null) {
        byId.set(String(tag.id), {
          id: tag.id,
          name: tag.name,
          color: tag.color,
        });
      }
    });
    localCreatedTags.forEach((tag) => {
      byId.set(String(tag.id), tag);
    });
    return Array.from(byId.values());
  }, [tagsData, savedTagItems, localCreatedTags]);

  const draftTags = useMemo(() => {
    return draftTagIds.map((id) => {
      const fromList = availableTags.find((t) => String(t.id) === String(id));
      const fromContact = savedTagItems.find(
        (t) => String(t.id) === String(id),
      );
      return fromList || fromContact || { id, name: `Tag #${id}`, color: null };
    });
  }, [draftTagIds, availableTags, savedTagItems]);

  const openSelector = useCallback(() => {
    if (disabled) return;
    setDraftTagIds((prev) => (prev.length > 0 ? prev : savedTagIds));
    setSearchQuery('');
    setDebouncedSearch('');
    setIsOpen(true);
  }, [disabled, savedTagIds]);

  const closeSelector = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setDebouncedSearch('');
  }, []);

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (!isOpen || !triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placement =
        spaceBelow < 250 && spaceAbove > spaceBelow ? 'top' : 'bottom';
      const width = Math.min(Math.max(rect.width, 240), window.innerWidth - 16);
      const left = Math.min(
        Math.max(8, rect.left),
        Math.max(8, window.innerWidth - width - 8),
      );
      setDropdownStyle({
        position: 'fixed',
        left,
        width,
        ...(placement === 'bottom'
          ? { top: rect.bottom + 4 }
          : { bottom: window.innerHeight - rect.top + 4 }),
      });
    };

    updatePosition();
    if (!isOpen) return undefined;
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        dropdownRef.current?.contains(event.target)
      ) {
        return;
      }
      closeSelector();
    };
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      closeSelector();
      addTagsRef.current?.focus();
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [isOpen, closeSelector]);

  const createTagMutation = useMutation({
    mutationFn: createCRMTag,
    onSuccess: (createdTag) => {
      setLocalCreatedTags((prev) => [...prev, createdTag]);
      setDraftTagIds((prev) => {
        if (prev.some((id) => String(id) === String(createdTag.id)))
          return prev;
        const next = [...prev, createdTag.id];
        persistTagIds(next);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['crm-tags', companyId] });
      setSearchQuery('');
      setDebouncedSearch('');
    },
    onError: () => {
      toast.error('Failed to create tag.');
    },
  });

  const saveTagsMutation = useMutation({
    mutationFn: ({ payload }) => {
      if (employeeId) {
        return manageEmployeeTags({
          companyId,
          employeeId,
          payload,
        });
      }
      return manageCRMContactTags({
        companyId,
        contactId,
        payload,
      });
    },
    onSuccess: (data) => {
      toast.success('Tags updated successfully!');
      if (employeeId) {
        const tagsFromResponse = Array.isArray(data?.tags) ? data.tags : [];
        const employee = { id: employeeId, ...data, tags: tagsFromResponse };
        patchEmployeeInCache(queryClient, companyId, employee);
        queryClient.invalidateQueries({
          queryKey: ['employeeDetails', companyId, employeeId],
        });
        queryClient.invalidateQueries({
          queryKey: ['companyEmployees', companyId],
        });
        onAssigned?.(employee);
      } else {
        queryClient.invalidateQueries({
          queryKey: ['crm-contact-detail', companyId, contactId],
        });
        queryClient.invalidateQueries({
          queryKey: ['crm-contact-timeline', companyId, contactId],
        });
        queryClient.invalidateQueries({
          queryKey: ['crm-contacts'],
        });
        queryClient.invalidateQueries({
          queryKey: ['crm-pipeline-board'],
        });
        syncPipelineBoardContact(queryClient, contactId, {
          tags: draftTags,
        });
      }
    },
    onError: (error) => {
      toast.error(
        getTagAssignErrorMessage(error, employeeId ? 'Employee' : 'Contact'),
      );
      setDraftTagIds(savedTagIds);
    },
  });

  const toggleTag = (tagId) => {
    hasUserEditedRef.current = true;
    setDraftTagIds((prev) => {
      const baseIds =
        prev.length > 0 || !savedTagIds.length ? prev : savedTagIds;
      const exists = baseIds.some((id) => String(id) === String(tagId));
      const next = exists
        ? baseIds.filter((id) => String(id) !== String(tagId))
        : [...baseIds, tagId];
      persistTagIds(next);
      return next;
    });
  };

  const commitLocalTags = useCallback(
    (tagIds) => {
      const items = tagIds.map((id) => {
        const fromList = availableTags.find((t) => String(t.id) === String(id));
        const fromSaved = savedTagItems.find(
          (t) => String(t.id) === String(id),
        );
        return fromList || fromSaved || { id, name: `Tag #${id}`, color: null };
      });
      onTagsChange?.(items);
    },
    [availableTags, savedTagItems, onTagsChange],
  );

  const persistTagIds = (tagIds) => {
    const payload = { tag_ids: tagIds.map(Number) };
    if (localOnly) {
      commitLocalTags(tagIds);
      return;
    }
    saveTagsMutation.mutate({ payload });
  };

  const removeTag = (tagToRemove) => {
    hasUserEditedRef.current = true;
    let targetId = tagToRemove?.id;
    if (targetId == null && tagToRemove?.name) {
      const match = availableTags.find(
        (t) => t.name?.toLowerCase() === tagToRemove.name.toLowerCase(),
      );
      if (match?.id != null) targetId = match.id;
    }

    setDraftTagIds((prev) => {
      const baseIds =
        prev.length > 0 || !savedTagIds.length ? prev : savedTagIds;
      const next = baseIds.filter((id) => {
        if (targetId != null && String(id) === String(targetId)) return false;
        if (tagToRemove?.id != null && String(id) === String(tagToRemove.id))
          return false;
        return true;
      });
      persistTagIds(next);
      return next;
    });
  };

  const handleCreateTag = () => {
    const name = searchQuery.trim();
    if (!name) return;
    const exists = availableTags.some(
      (tag) => tag.name.toLowerCase() === name.toLowerCase(),
    );
    if (exists) return;
    createTagMutation.mutate({
      companyId,
      payload: { name, color: randomTagColor() },
    });
  };

  const handleListScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 30) {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    }
  };

  const trimmedSearch = searchQuery.trim();
  const showCreateOption =
    trimmedSearch &&
    !availableTags.some(
      (tag) => tag.name.toLowerCase() === trimmedSearch.toLowerCase(),
    );

  const isSaving = saveTagsMutation.isPending;
  const isCompact = appearance === 'compact';

  const chipsToShow = useMemo(() => {
    if (draftTagIds.length > 0) return draftTags;
    if (
      !savedTagIds.length &&
      savedTagItems.length > 0 &&
      !hasUserEditedRef.current
    ) {
      return savedTagItems;
    }
    return draftTags;
  }, [draftTagIds, draftTags, savedTagIds, savedTagItems]);

  return (
    <>
      <div
        ref={triggerRef}
        className={`relative w-full min-w-0 ${
          isCompact
            ? ''
            : `min-h-[44px] rounded-xl bg-[#FFFFFF] border transition-colors ${
                disabled
                  ? 'border-border-default opacity-60 cursor-not-allowed'
                  : isOpen
                    ? 'border-[#50AB3D] cursor-text'
                    : 'border-border-default hover:border-[#50AB3D] cursor-text'
              }`
        }`}
        onClick={() => {
          if (isCompact || disabled) return;
          openSelector();
          document.getElementById(searchInputId)?.focus();
        }}
      >
        <div
          className={`flex flex-wrap items-center gap-1.5 ${
            isCompact ? 'pr-0' : 'py-2 pl-3 pr-10'
          }`}
        >
          {chipsToShow.map((tag) => (
            <span
              key={tag.id ?? tag.name}
              className={`inline-flex max-w-full items-center gap-1 rounded-[8px] px-2.5 py-1 text-[12px] font-medium ${!tag.color ? 'bg-[#EBF4EA] text-[#50AB3D]' : ''}`}
              style={
                tag.color
                  ? {
                      backgroundColor: `${tag.color}20`,
                      color: tag.color,
                    }
                  : undefined
              }
            >
              <span className="truncate">{tag.name}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(tag);
                  }}
                  className="shrink-0 hover:opacity-70 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#50AB3D] leading-none"
                  style={tag.color ? { color: tag.color } : undefined}
                  disabled={isSaving}
                  aria-label={`Remove ${tag.name}`}
                >
                  &times;
                </button>
              )}
            </span>
          ))}
          {!disabled && !isCompact && (
            <input
              id={searchInputId}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (showCreateOption) handleCreateTag();
                }
              }}
              onFocus={openSelector}
              placeholder={
                chipsToShow.length === 0 ? placeholder : 'Add more...'
              }
              className="flex-1 min-w-[100px] bg-transparent text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none py-1 cursor-text"
              disabled={disabled || isSaving}
              autoComplete="off"
            />
          )}
          {disabled && chipsToShow.length === 0 && (
            <span className="text-sm text-[#868686] italic py-1">
              {emptyLabel}
            </span>
          )}
          {!disabled && isCompact && (
            <button
              ref={addTagsRef}
              type="button"
              onClick={openSelector}
              disabled={isSaving}
              className="inline-flex items-center gap-1 rounded-[8px] px-2 py-1 text-[13px] font-medium text-[#868686] hover:text-[#50AB3D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#50AB3D]"
            >
              {chipsToShow.length === 0 ? 'Click to add tags' : '+ Add tags'}
            </button>
          )}
          {isSaving && <span className="text-xs text-[#868686]">Saving…</span>}
        </div>
        {!disabled && !isCompact && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              if (isOpen) closeSelector();
              else openSelector();
            }}
            className="absolute right-0 top-0 bottom-0 px-3 flex items-center justify-center cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#50AB3D] z-10"
            aria-label="Toggle tags dropdown"
            disabled={isSaving}
          >
            <ChevronIcon open={isOpen} />
          </button>
        )}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ ...dropdownStyle, overscrollBehavior: 'contain' }}
            data-portal-dropdown="true"
            className="z-[99999] flex flex-col max-h-[min(250px,70vh)] bg-[#FFFFFF] border border-border-default rounded-xl shadow-lg overflow-hidden"
          >
            {isCompact && (
              <div className="shrink-0 border-b border-[#EBF4EA] p-2">
                <input
                  id={searchInputId}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (showCreateOption) handleCreateTag();
                    }
                  }}
                  placeholder="Search tags"
                  className="w-full rounded-[8px] border border-[#EBF4EA] bg-white px-3 py-2 text-[14px] text-[#1F2241] placeholder:text-[#868686] focus:outline-none focus:border-[#50AB3D]"
                  autoComplete="off"
                  autoFocus
                />
              </div>
            )}
            <div
              className="flex-1 min-h-0 overflow-y-auto custom-scrollbar py-1"
              onScroll={handleListScroll}
            >
              {isTagsLoading ? (
                <div className="p-3 text-center text-xs text-text-muted">
                  Loading tags...
                </div>
              ) : availableTags.length > 0 ? (
                <>
                  {availableTags.map((tag) => {
                    const isSelected = draftTagIds.some(
                      (id) => String(id) === String(tag.id),
                    );
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        className={`w-full flex items-center gap-2 text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                          isSelected
                            ? 'bg-[#EBF4EA] text-[#50AB3D]'
                            : 'text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D]'
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => toggleTag(tag.id)}
                      >
                        <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center border border-[#50AB3D] rounded">
                          {isSelected && (
                            <svg
                              className="w-3 h-3 text-[#50AB3D]"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M4.5 12.75l6 6 9-13.5"
                              />
                            </svg>
                          )}
                        </span>
                        <span className="truncate">{tag.name}</span>
                      </button>
                    );
                  })}
                  {isFetchingNextPage && (
                    <div className="p-2 text-center text-xs text-text-muted">
                      Loading more...
                    </div>
                  )}
                </>
              ) : showCreateOption ? null : (
                <div className="p-3 text-center text-xs text-text-muted">
                  No tags found
                </div>
              )}

              {showCreateOption && (
                <button
                  type="button"
                  className="w-full flex items-center gap-2 text-left px-4 py-2.5 text-sm font-medium text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D] transition-colors"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleCreateTag}
                  disabled={createTagMutation.isPending}
                >
                  {createTagMutation.isPending
                    ? 'Creating tag...'
                    : `Create "${trimmedSearch}"`}
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
